import { MotionDetectionState } from '../types';
import { GpsPosition } from '../types/platform';
import { isGeolocationAllowedByPolicy } from '../utils/geolocationPolicy';

/**
 * Motion Detection Service
 * Detects vehicle movement via accelerometer + GPS
 * Prevents ePRF updates on driver tablet when vehicle is moving (road safety compliance)
 */

interface MotionListeners {
  (state: MotionDetectionState): void;
}

type GpsPositionListener = (pos: GpsPosition) => void;

let isListening = false;
let accelerometerData: { x: number; y: number; z: number } | null = null;
let lastGpsSpeed = 0;
let lastGpsPosition: GpsPosition | null = null;
let listeners: Set<MotionListeners> = new Set();
let gpsListeners: Set<GpsPositionListener> = new Set();

const MOVEMENT_THRESHOLD = 2; // m/s² acceleration threshold
const GPS_SPEED_THRESHOLD = 5; // km/h threshold (>5 km/h = moving)
const POLL_INTERVAL = 2000; // Check every 2 seconds

/**
 * Start motion detection
 * Subscribes to DeviceMotionEvent + geolocation
 */
export function startMotionDetection(): void {
  if (isListening) return;

  console.log('🚀 Starting motion detection...');
  isListening = true;

  // Request DeviceMotionEvent permission (iOS 13+)
  if (
    typeof DeviceMotionEvent !== 'undefined' &&
    typeof (DeviceMotionEvent as any).requestPermission === 'function'
  ) {
    (DeviceMotionEvent as any)
      .requestPermission()
      .then((permission: string) => {
        if (permission === 'granted') {
          window.addEventListener('devicemotion', handleDeviceMotion);
        }
      })
      .catch(console.error);
  } else {
    // Fallback for Android/older iOS
    window.addEventListener('devicemotion', handleDeviceMotion);
  }

  // Start GPS polling
  if (navigator.geolocation && isGeolocationAllowedByPolicy()) {
    startGpsPolling();
  }
}

/**
 * Stop motion detection
 */
export function stopMotionDetection(): void {
  if (!isListening) return;

  console.log('🛑 Stopping motion detection');
  isListening = false;

  window.removeEventListener('devicemotion', handleDeviceMotion);
  stopGpsPolling();
  listeners.clear();
}

/**
 * Handle device motion events (accelerometer)
 */
function handleDeviceMotion(event: DeviceMotionEvent): void {
  if (!event.acceleration) return;

  const x = event.acceleration.x ?? 0;
  const y = event.acceleration.y ?? 0;
  const z = event.acceleration.z ?? 0;

  // Calculate magnitude of acceleration
  const magnitude = Math.sqrt(x * x + y * y + z * z);

  accelerometerData = { x, y, z };

  // Notify listeners if significant movement detected
  if (magnitude > MOVEMENT_THRESHOLD) {
    notifyListeners();
  }
}

/**
 * Start GPS polling for speed
 */
let gpsWatchId: number | null = null;

function startGpsPolling(): void {
  try {
    gpsWatchId = navigator.geolocation.watchPosition(
      (position) => {
        // Store speed if available
        lastGpsSpeed = position.coords.speed ? position.coords.speed * 3.6 : 0; // convert m/s to km/h

        // Build GpsPosition and notify GPS subscribers
        const gpsPos: GpsPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          heading: position.coords.heading ?? undefined,
          speedKmh: lastGpsSpeed,
          accuracyMetres: position.coords.accuracy ?? undefined,
          timestamp: new Date().toISOString(),
        };
        lastGpsPosition = gpsPos;
        gpsListeners.forEach((l) => l(gpsPos));

        // Notify motion listeners if moving
        if (lastGpsSpeed > GPS_SPEED_THRESHOLD) {
          notifyListeners();
        }
      },
      (error) => {
        console.warn('Geolocation error:', error.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 1000,
      }
    );
  } catch (error) {
    gpsWatchId = null;
    console.warn('Geolocation watch blocked by browser policy', error);
  }
}

/**
 * Stop GPS polling
 */
function stopGpsPolling(): void {
  if (gpsWatchId !== null) {
    navigator.geolocation.clearWatch(gpsWatchId);
    gpsWatchId = null;
  }
}

/**
 * Get current motion state
 */
export function getMotionState(): MotionDetectionState {
  const isMoving =
    (accelerometerData &&
      Math.sqrt(
        accelerometerData.x ** 2 +
          accelerometerData.y ** 2 +
          accelerometerData.z ** 2
      ) > MOVEMENT_THRESHOLD) ||
    lastGpsSpeed > GPS_SPEED_THRESHOLD;

  return {
    isMoving,
    accelerometerData: accelerometerData
      ? {
          timestamp: new Date().toISOString(),
          ...accelerometerData,
        }
      : undefined,
    gpsSpeed: lastGpsSpeed,
    lastUpdateTime: new Date().toISOString(),
  };
}

/**
 * Subscribe to motion state changes
 */
export function onMotionChange(callback: MotionListeners): () => void {
  listeners.add(callback);

  // Return unsubscribe function
  return () => {
    listeners.delete(callback);
  };
}

/**
 * Subscribe to raw GPS position updates.
 * Reuses the existing watchPosition — does not start a second GPS watch.
 * Returns an unsubscribe function.
 */
export function onGpsPosition(callback: GpsPositionListener): () => void {
  gpsListeners.add(callback);
  // Immediately emit the last known position if available
  if (lastGpsPosition) callback(lastGpsPosition);
  return () => {
    gpsListeners.delete(callback);
  };
}

/**
 * Get the last known GPS position (may be null before first fix).
 */
export function getLastGpsPosition(): GpsPosition | null {
  return lastGpsPosition;
}

/**
 * Notify all listeners of motion state
 */
function notifyListeners(): void {
  const state = getMotionState();
  listeners.forEach((listener) => listener(state));
}

/**
 * Check if crew can edit ePRF
 * Rules:
 * - If driver role + vehicle moving → LOCK
 * - If paramedic role + 2+ crew members → UNLOCK (passenger can edit)
 * - If paramedic role + solo crew → LOCK
 * - If not moving → UNLOCK
 */
export function canEditEprf(
  userRole: string,
  crewCount: number,
  isVehicleMoving: boolean
): { allowed: boolean; reason?: string } {
  // If vehicle not moving, always allow
  if (!isVehicleMoving) {
    return { allowed: true };
  }

  // If driver role and vehicle moving → LOCK
  if (userRole === 'Driver' || userRole === 'Paramedic' && crewCount === 1) {
    return {
      allowed: false,
      reason: 'ePRF locked: Vehicle in motion. Passenger or stationary to edit.',
    };
  }

  // If 2+ crew members → UNLOCK (passenger can edit)
  if (crewCount >= 2) {
    return { allowed: true };
  }

  return {
    allowed: false,
    reason: 'ePRF locked: Cannot edit while vehicle is moving.',
  };
}

/**
 * Get motion-aware ePRF edit restrictions
 */
export function getEprfEditRestrictions(
  userRole: string,
  crewCount: number,
  isVehicleMoving: boolean
): {
  canEdit: boolean;
  canRead: boolean;
  lockedFields: string[];
  reason?: string;
} {
  const vehicleRestriction = canEditEprf(userRole, crewCount, isVehicleMoving);

  return {
    canEdit: vehicleRestriction.allowed,
    canRead: true, // Always allow reading
    lockedFields: vehicleRestriction.allowed
      ? []
      : [
          'history',
          'assessment',
          'treatments',
          'diagnosis',
          'governance',
          'clinicalDecision',
        ], // Critical sections locked while moving
    reason: vehicleRestriction.reason,
  };
}

/**
 * Mock motion service for testing (useful for web-only testing)
 */
export function startMockMotionDetection(moveEvery: number = 5000): void {
  console.log('📡 Starting MOCK motion detection (testing only)');

  setInterval(() => {
    // Simulate random movement
    accelerometerData = {
      x: Math.random() * 4 - 2,
      y: Math.random() * 4 - 2,
      z: Math.random() * 4 - 2,
    };

    // Simulate random speed
    lastGpsSpeed = Math.random() * 60; // 0-60 km/h

    if (Math.random() > 0.5) {
      notifyListeners();
    }
  }, moveEvery);
}

/**
 * Debug helper to force motion state
 */
export function setDebugMotionState(
  isMoving: boolean,
  speed?: number,
  acceleration?: { x: number; y: number; z: number }
): void {
  lastGpsSpeed = speed || (isMoving ? 10 : 0);
  accelerometerData = acceleration || {
    x: isMoving ? 2 : 0,
    y: isMoving ? 2 : 0,
    z: isMoving ? 2 : 0,
  };

  notifyListeners();

  console.log(`🔍 Debug motion state set: isMoving=${isMoving}, speed=${lastGpsSpeed}km/h`);
}
